/**
 * StructuredProposalCard の人数按分表示で使う、単位ごとの丸めロジック。
 * Design L-10 反映: 返り値は {value: number, display: string} の形。
 * 詳細: docs/design/v03-ai-menu-dev-improvements.md §6.3
 */
export type RoundedQuantity = { value: number; display: string };

// Test H-3 反映: 単位辞書拡張。整数丸め (精度1) / 0.5単位 / 0.25単位 / 0.1単位の4カテゴリに分類する
const INTEGER_UNITS = new Set([
  'g', 'ml', '個', '束', '玉', '膳', '枚', '杯', '本', '袋', '丁', '缶', 'パック', '切れ', '尾', '房',
]);
const DECIMAL_0_1_UNITS = new Set(['kg', 'L']);
const DECIMAL_0_5_UNITS = new Set(['大さじ', '小さじ', '合']);
const DECIMAL_0_25_UNITS = new Set(['カップ']);
// 大さじ/小さじ は「大さじ1」のようにレシピ表記の慣例で単位が数値の前に付く
const PREFIX_UNITS = new Set(['大さじ', '小さじ']);

const precisionFor = (unit: string): number | null => {
  if (INTEGER_UNITS.has(unit)) return 1;
  if (DECIMAL_0_1_UNITS.has(unit)) return 0.1;
  if (DECIMAL_0_5_UNITS.has(unit)) return 0.5;
  if (DECIMAL_0_25_UNITS.has(unit)) return 0.25;
  return null;
};

const roundToPrecision = (value: number, precision: number): number => {
  const factor = 1 / precision;
  return Math.round(value * factor) / factor;
};

const formatDisplay = (value: number, unit: string): string =>
  PREFIX_UNITS.has(unit) ? `${unit}${value}` : `${value}${unit}`;

/**
 * 分量を単位に応じた精度で丸める。0は「-」、丸めると0になる非ゼロの微量は「少々」を返す
 * (最小表示単位未満、Design L-10)。未知の単位は無変換で返し console.warn する。
 */
export const roundQuantityByUnit = (rawValue: number, unit: string): RoundedQuantity => {
  if (rawValue === 0) return { value: 0, display: '-' };

  const precision = precisionFor(unit);
  if (precision === null) {
    console.warn(`[round-quantity-by-unit] 未知の単位です: ${unit}`);
    return { value: rawValue, display: `${rawValue} ${unit}` };
  }

  const rounded = roundToPrecision(rawValue, precision);
  if (rounded === 0) {
    return { value: rawValue, display: '少々' };
  }

  return { value: rounded, display: formatDisplay(rounded, unit) };
};
